import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { RemovableRef, useLocalStorage } from "@vueuse/core";

import type { AuthenticationResult, JsonWebToken } from "@tmi/types/authentication";
import type { ApiFetchResponse, TenantContextPayload } from "@tmi/types/api/fetch";
import { DateTime } from "luxon";
import { normalizeDateTime, utcNow } from "@tmi/utils/date-time";
import {
    clearSessionCache,
    defaultReasonForAbortingApiCall,
    goFetch,
    isSuccessState
} from "@/utils/network-utils";

import { clearToken, setToken } from "@/api/global";
import { purgeUserScopedStorage } from "@/router/user-scoped-storage";
import { clientStateManager } from "@/services/error-services";
import {
    SESSION_ENDED_ELSEWHERE_EVENT,
    SESSION_EXPIRED_EVENT,
    type SessionEndedElsewhereDetail
} from "@/services/session-check";
import { deriveCacheOwnerId } from "@/utils/jwt-identity";
import { setStorageOwner } from "@/utils/storage-owner";

import { ApiError, AuthenticationFailed, InternalError } from "@tmi/types/api/responses";
import { RuntimeException } from "@tmi/types/errors";
import { ApplicationId } from "@/constants";

import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

function parseJwtExpiration( jwt: string ): DateTime | null {
    try {
        // JWT structure: header.payload.signature
        const parts = jwt.split( '.' );
        if ( parts.length !== 3 ) {
            return null;
        }

        // Decode the payload (second part) from base64url
        const payload = JSON.parse( atob( parts[ 1 ].replace( /-/g, '+' ).replace( /_/g, '/' ) ) );

        // Extract exp claim (Unix timestamp in seconds)
        if ( payload?.exp ) {
            return DateTime.fromSeconds( payload.exp );
        }

        return null;
    } catch ( err ) {
        logger.error( `Failed to parse JWT expiration: ${ err }` );
        return null;
    }
}

function isValidJwt( jwt: string ): boolean {
    const expiration = parseJwtExpiration( jwt );
    if ( !expiration ) {
        return false;
    }

    return expiration > DateTime.utc();
}

function parseStoredToken( rawToken: string | null ): JsonWebToken | null {
    if ( !rawToken ) {
        return null;
    }

    try {
        return JSON.parse( rawToken ) as JsonWebToken;
    } catch ( err ) {
        logger.warn( `Failed to parse stored auth token: ${ err }` );
        // tmi-6gh (LD3): an unparseable token must reset coherently, not just drop the key —
        // this catch runs inside the module-scope watch's immediate evaluation, before the
        // boot-hygiene check below can see the token.
        reset();
        return null;
    }
}

function resolveTokenExpiration( storedToken: JsonWebToken ): DateTime | null {
    const storedTtl = normalizeDateTime( storedToken.timeToLive ?? undefined );
    if ( storedTtl.isValid ) {
        return storedTtl;
    }

    return storedToken.jwt ? parseJwtExpiration( storedToken.jwt ) : null;
}

function assignToken( token: string | JsonWebToken ): void {
    logger.debug( `[ auth-store ].assignToken( "${ token }" ) called` );

    let tempHolder: JsonWebToken | undefined;
    if ( typeof token === 'string' ) {
        // Parse the JWT to extract expiration
        const expiration = parseJwtExpiration( token );

        tempHolder = {
            jwt: token,
            sessionId: undefined,
            timeToLive: expiration
        };
    } else {
        // Store the value of the shared JWT token
        tempHolder = token;
    }

    lsToken.value = JSON.stringify( tempHolder );
}

function parseResponse( payload: AuthenticationResult): JsonWebToken | undefined {
    let result: JsonWebToken | undefined = undefined;
    const authResult = payload?.authResult;
    if ( authResult ) {
        const tokenValue = authResult.token ?? undefined;
        logger.debug( `Received token: ${ tokenValue }` );

        // track the session ID for the JsonWebToken
        const sessionId = authResult.sessionId ?? undefined;
        logger.debug( `Received session ID: ${ sessionId }` );

        // track the TTL for the JsonWebToken
        const ttl = normalizeDateTime( authResult.validTo ?? undefined );
        logger.debug( `Received ttl: ${ ttl }` );

        // create the JsonWebToken container from the data available from the Flagship authentication response
        result = {
            jwt: tokenValue,
            sessionId: sessionId,
            timeToLive: ttl
        };
    }

    return result;
}

/**
 * Why a sign-in attempt failed, in the terms the member can act on (UX roadmap #25). Every failure
 * used to read "Invalid email or password", so a member on a flaky connection or during an API
 * incident retyped, reset their password or called support when the fix was to retry.
 */
export type SignInFailureKind = 'credentials' | 'timeout' | 'offline' | 'server';

/** What `authenticate()` throws; Login.vue picks its copy from `kind`. */
export class SignInFailed extends AuthenticationFailed {
    readonly kind: SignInFailureKind;

    constructor( kind: SignInFailureKind, msg: string ) {
        super( msg );
        this.kind = kind;
    }
}

/**
 * Maps what `goFetch( { forceThrowExceptions: true } )` rejects with onto a SignInFailureKind.
 * For a bad email or password `authentication/initiateauth` answers 404 ("No user was found"),
 * which goFetch rejects as NotFound; that and the other 4xx answers are about the account.
 */
export function classifySignInFailure( err: unknown ): SignInFailureKind {
    // Nothing reached the server, whatever the rejection looks like.
    if ( typeof navigator !== 'undefined' && navigator.onLine === false ) {
        return 'offline';
    }

    // goFetch aborts with this string after its time budget; a DOMException is the browser's own abort.
    if ( err === defaultReasonForAbortingApiCall
        || ( typeof DOMException !== 'undefined' && err instanceof DOMException && ( err.name === 'AbortError' || err.name === 'TimeoutError' ) ) ) {
        return 'timeout';
    }

    // fetch rejects with a TypeError when no response arrived at all (connection dropped, DNS).
    if ( err instanceof TypeError ) {
        return 'offline';
    }

    // 501 is excluded: the auth service answers "User is not active" with NotImplemented, which
    // is an account problem for support, not an outage the member should wait out.
    if ( err instanceof InternalError || ( err instanceof ApiError && err.code >= 500 && err.code !== 501 ) ) {
        return 'server';
    }

    return 'credentials';
}

/**
 * Timeouts and server failures change what the member sees, so they go to the client error queue
 * (the logger is a no-op in production). Wrong credentials and offline devices are not incidents:
 * reporting them is noise, and an offline report could not be sent anyway.
 */
function reportSignInFailure( failure: SignInFailed, cause: unknown, endpoint: string ): SignInFailed {
    if ( failure.kind === 'server' || failure.kind === 'timeout' ) {
        clientStateManager.queueClientError( cause instanceof InternalError ? cause : failure, {
            endpoint,
            kind: failure.kind,
            status: cause instanceof RuntimeException ? cause.code : undefined
        } );
    }

    return failure;
}

/** Dispatched on `window` whenever the auth state is reset; user-scoped stores drop their in-memory copies on it (LD1). */
export const AUTH_RESET_EVENT = 'tmi:auth-reset';

export interface AuthResetOptions {
    /**
     * data-sync-3: the raw `token` value in localStorage when the teardown started. Shared storage is left alone when
     * it holds anything else by the time the reset runs: another member signed in meanwhile (a frozen background tab
     * handling a sign-out from another tab late), and removing the token or purging would sign them out too. The tab
     * that signed out has already purged shared storage. The in-memory reset and AUTH_RESET_EVENT still happen.
     */
    onlyIfStoredTokenIs?: string | null;
}

/**
 * Drops everything user-scoped except the token: the session check memo, the user-scoped localStorage keys, and (through
 * AUTH_RESET_EVENT) every in-memory copy of them. reset() runs it once the token is gone; a token handoff runs it after
 * assigning the new member's token (boot-auth-2), which used to leave the previous member's recent tickers, video
 * progress and favourites in place. `purgeStorage: false` leaves localStorage alone (see AuthResetOptions).
 */
export function resetUserScopedSession( { purgeStorage = true }: { purgeStorage?: boolean } = {} ): void {
    clearSessionCache();
    if ( purgeStorage ) {
        purgeUserScopedStorage();
    }
    // Native removeItem is invisible to same-document VueUse refs — tell the
    // user-scoped stores to drop their in-memory copies too (LD1).
    window.dispatchEvent( new CustomEvent( AUTH_RESET_EVENT ) );
}

/** The raw `token` value in localStorage now, for AuthResetOptions.onlyIfStoredTokenIs. */
function readStoredToken(): string | null {
    return localStorage.getItem( TOKEN_LOCALSTORAGE_KEY );
}

function reset( { onlyIfStoredTokenIs }: AuthResetOptions = {} ): void {
    const sharedStorageIsOurs: boolean = undefined === onlyIfStoredTokenIs || readStoredToken() === onlyIfStoredTokenIs;
    if ( sharedStorageIsOurs ) {
        lsToken.value = null;
        // VueUse's useStorage dispatches a same-window StorageEvent on every write and pauses
        // its own write-watcher until nextTick while handling it. When reset() runs *inside*
        // the authToken watch triggered by a token write (R42: live token overwritten by an
        // expired one), the `null` above is swallowed by that pause and the dead token would
        // survive in localStorage — remove it natively so token-clear never depends on it.
        localStorage.removeItem( TOKEN_LOCALSTORAGE_KEY );
    }
    // Superseded: lsToken is not written either. VueUse writes it through to storage, so a `null` here would remove the
    // other member's token all the same; it follows their token through the storage event instead.
    resetUserScopedSession( { purgeStorage: sharedStorageIsOurs } );
}

const TOKEN_LOCALSTORAGE_KEY: string = 'token';

// Assigned in the "assignToken" action
const lsToken: RemovableRef< string | null > = useLocalStorage( TOKEN_LOCALSTORAGE_KEY, null );

/**
 * UX roadmap #16: `token` compares the stored expiry with the clock, which is not reactive, so an expired token read as
 * valid until something wrote localStorage. The member kept the signed-in app while every request failed. Bumping
 * this re-evaluates it: a timer set for the token's expiry, and the tab being shown again (a sleeping laptop's timers
 * fire late, after the member is back on the page).
 */
const expiryCheck = ref< number >( 0 );
let expiryTimer: ReturnType< typeof setTimeout > | null = null;
// setTimeout fires at once for a delay past 2^31 - 1 ms (about 24.8 days); a longer-lived token re-arms on the way.
const MaxExpiryTimerDelayMilliseconds: number = 2_147_483_647;

function recheckTokenExpiry(): void {
    expiryCheck.value++;
}

function armExpiryTimer( expiration: DateTime | null | undefined ): void {
    if ( null !== expiryTimer ) {
        clearTimeout( expiryTimer );
        expiryTimer = null;
    }

    if ( !expiration?.isValid ) {
        return;
    }

    const delay: number = Math.min( Math.max( expiration.toMillis() - Date.now(), 0 ), MaxExpiryTimerDelayMilliseconds );
    expiryTimer = setTimeout( () => {
        expiryTimer = null;
        recheckTokenExpiry();
    }, delay );
}

const token = computed< JsonWebToken | null >( () => {
    // Read for the dependency only: see expiryCheck.
    void expiryCheck.value;
    logger.debug( `TokenManager.getToken(): this._token[ ${ JSON.stringify( lsToken.value ) } ], this._tokenKeyName[ ${ TOKEN_LOCALSTORAGE_KEY } ]` );
    const localToken = parseStoredToken( lsToken.value );

    if ( localToken ) {
        const expiration = resolveTokenExpiration( localToken );
        if ( expiration && utcNow() < expiration ) {
            return {
                ...localToken,
                timeToLive: expiration
            };
        }
    }

    // Token is invalid/expired - return null (reset handled in watch)
    return null;
} );

const authToken = computed< string | null >( () => token.value?.jwt ?? null );
// Track changes to the authentication token to automatically assign the Bearer token in any pinia-colada calls
watch( authToken, ( newValue, oldValue ) => {
    // UX roadmap #63: read state and bell acknowledgements are kept per member (utils/storage-owner.ts).
    setStorageOwner( deriveCacheOwnerId( newValue ) );

    if ( newValue ) {
        setToken( newValue );
    } else {
        clearToken();
        // If we had a token, but now it's null (expired/invalid), clear localStorage
        if ( oldValue && !newValue && lsToken.value !== null ) {
            reset();
            // UX roadmap #16: the session ran out under the member. A sign-out clears lsToken first, so it never gets here.
            window.dispatchEvent( new CustomEvent( SESSION_EXPIRED_EVENT ) );
        }
    }
}, { immediate: true } );

// Re-armed for every token, and cleared once there is none. `token` is a new object on every evaluation, so the
// timer that outlives the longest setTimeout delay re-arms here when it fires.
watch( token, current => armExpiryTimer( current?.timeToLive ), { immediate: true } );

/**
 * UX roadmap #63: another tab removed the token this tab was signed in with. The storage event only reaches the other
 * tabs, and VueUse has already nulled lsToken by the time the token watch runs, so that watch cannot tell this from a
 * sign-out here: it neither resets nor reports an expiry. Tell the Application store, which ends the session the way an
 * expiry does. Only for a token that named a member: a stray or unparseable value never ended a session.
 */
function onTokenRemovedElsewhere( event: StorageEvent ): void {
    if ( TOKEN_LOCALSTORAGE_KEY !== event.key || null !== event.newValue || null === event.oldValue ) {
        return;
    }
    if ( event.storageArea && event.storageArea !== globalThis.localStorage ) {
        return;
    }
    // data-sync-3: a frozen background tab handles its queued storage events late. When a newer sign-in has already
    // stored another token, this removal is history: ending the session for it would remove that member's token.
    if ( null !== readStoredToken() ) {
        return;
    }

    let previous: JsonWebToken | null = null;
    try {
        previous = JSON.parse( event.oldValue ) as JsonWebToken | null;
    } catch {
        return;
    }
    if ( !previous?.jwt ) {
        return;
    }

    const expiration: DateTime | null = resolveTokenExpiration( previous );
    const detail: SessionEndedElsewhereDetail = { expired: !expiration || utcNow() >= expiration };
    window.dispatchEvent( new CustomEvent< SessionEndedElsewhereDetail >( SESSION_ENDED_ELSEWHERE_EVENT, { detail } ) );
}

if ( typeof document !== 'undefined' ) {
    window.addEventListener( 'storage', onTokenRemovedElsewhere );
    document.addEventListener( 'visibilitychange', () => {
        if ( 'visible' === document.visibilityState ) {
            recheckTokenExpiry();
        }
    } );
    // A page restored from the back/forward cache had its timers stopped for as long as it was away.
    window.addEventListener( 'pageshow', ( event: PageTransitionEvent ) => {
        if ( event.persisted ) {
            recheckTokenExpiry();
        }
    } );
}

// tmi-6gh (LD3): an expired-but-parseable stored token must not linger — boot coherently
// signed out (token cleared + user-scoped purge + store reset together). The
// unparseable case is handled inside parseStoredToken's catch.
if ( lsToken.value !== null && token.value === null ) {
    reset();
}

export const useAuthStore = defineStore( 'authentication', () => {
    const isAuthenticated = computed< boolean >( () => !!( token.value?.jwt ) );

    async function authenticate( credentials: any ) {
        const username: string = credentials?.username ?? undefined;
        const password: string = credentials?.password ?? undefined;

        logger.debug( `[AuthHandler].authenticate( ${ username }, ${ password } )` );

        if ( !( username && password ) ) {
            throw new Error( `The username & password are required: ${ JSON.stringify( credentials ) }` );
        }

        const endpoint: string = `authentication/initiateauth`;
        let response: ApiFetchResponse< AuthenticationResult > | null;
        try {
            response =
                await goFetch< AuthenticationResult, TenantContextPayload >( {
                    endpoint,
                    payload: {
                        secrets: [
                            { key: "username", value: username },
                            { key: "userpassword", value: password }
                        ]
                    },
                    // Without it goFetch resolves network failures to null, which then read as a
                    // wrong password. No token is sent, so a 401 here is an ApiError: it never
                    // reaches goFetch's clearSessionCache / queueClientError Unauthorized branch.
                    forceThrowExceptions: true
                } );
        } catch ( err: any ) {
            throw reportSignInFailure( new SignInFailed( classifySignInFailure( err ), `${ err?.message ?? err }` ), err, endpoint );
        }

        const authToken: JsonWebToken | undefined =
            response?.data && isSuccessState( response.status ) ? parseResponse( response.data ) : undefined;
        if ( !authToken?.jwt ) {
            // An OK answer without a token is not something the member typed wrong. Before, this
            // either threw the wrong-password error or silently left the member on the form.
            throw reportSignInFailure( new SignInFailed( 'server', 'The sign-in response carried no token' ), response, endpoint );
        }

        assignToken( authToken );
    }

    /**
     * Clean up any current session information held on the server in a consistent manner and then
     * remove all artifacts of being logged in from the current browser (localstorage)
     */
    async function logout( options?: AuthResetOptions ) {
        // regardless of the above result, clear out the current token from localstorage to avoid odd "auth" issues
        reset( options );
    }

    return {
        // ACTIONS
        assignToken,
        authenticate,
	isValidJwt,
        logout,
        readStoredToken,
        resetUserScopedSession,

        // STATE
        authToken,
        isAuthenticated
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useAuthStore, import.meta.hot ) );
}